import { useCallback, useEffect, useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useExitIntent } from 'use-exit-intent';
import Api from '~/api';
import { images } from '~/themes/variables';
import { generateMobileHeader } from './helpers';
import { getTimeElapsed } from '~/utils/date';
import { Status } from '~/types/common';
import { $isFullscreenAtom, $isPausedAtom, $shouldDisplayFeedbackAtom, $watchedSecondsAtom, } from '~/atoms';
let jwplayer = null;
let helloSeekPoint = 0;
export const VIDEO_CONFIG = {
    aspectRatio: '16:9',
    autostart: true,
    mute: false,
    key: `${APP.PLAYER_LICENSE_KEY}`,
    skin: {
        name: 'oneroom',
    },
    logo: {
        file: images.playerCredit,
        position: 'control-bar',
        hide: true,
    },
    abouttext: `Powered by OneRoom ${APP.VER}`,
    aboutlink: __ONEROOM.WEBSITE,
    primary: 'html5',
    loadAndParseHlsMetadata: true,
    preload: 'auto',
    playbackRateControls: true,
};
export const VIDEO_PLAYER_ID = 'jwplayer-0';
export const INSTANCE_ID = 'mediaPlayer';
const ERROR_MESSAGES = {
    NO_CONNECTION: 'We apologize for the interruption.\nIf your connection does not return soon, there will be a full recording available here at the conclusion of the service.\n\nThank you for your patience.',
    CAMERA_UNAVAILABLE: 'This camera is temporarily unavailable',
    MAIN_CAMERA_UNAVAILABLE: 'Streaming Error',
};
const convertCameraToPlaylist = (camera) => ({
    file: camera?.url,
    image: camera?.thumbnailUrl,
    cameraId: camera?.cameraId,
    status: camera?.status,
});
const hideShowElementsOnPlayOrPause = (isHide) => {
    if (!isMobile) {
        const overlayTitle = document.querySelector('#overlay-title');
        if (overlayTitle)
            overlayTitle.style.display = isHide ? 'none' : 'block';
        const controlsInMiddleOverlay = document.querySelector('.jw-skin-oneroom .jw-controls .jw-display');
        if (controlsInMiddleOverlay)
            controlsInMiddleOverlay.style.display = isHide ? 'none' : '';
    }
    const controlsInMiddleBackdrop = document.querySelector('.jw-controls-backdrop');
    if (controlsInMiddleBackdrop)
        controlsInMiddleBackdrop.style.display = isHide ? 'none' : 'block';
};
const getValidCameras = (cameras) => cameras.filter((camera) => camera.status === Status.streaming ||
    camera.status === Status.archived ||
    camera.status === Status.archivedS3);
let fullscreenCurrentCamId = 0;
export function useMediaPlayer(eventId, eventImage, eventTitle, eventStatus, eventDuration, cameras, setCamList, startTime, eventSubHeading, onDemand, registrationRequired, rewind) {
    const { unsubscribe, registerHandler } = useExitIntent({
        desktop: {
            // delayInSecondsToTrigger: 10,
            triggerOnMouseLeave: true,
            triggerOnIdle: false,
            useBeforeUnload: true,
        },
        mobile: {
            delayInSecondsToTrigger: 10,
            triggerOnIdle: false,
        },
    });
    const [timeToWatchCamera, setTimeToWatchCamera] = useState(0);
    const [intervalStreamingId] = useState('');
    const [pauseTimeoutId, setPauseTimeoutId] = useState(null);
    const [jwplayerError, setJwplayerError] = useState('');
    const [playerId, setPlayerId] = useState('jwplayer');
    const [currentCamId, setCurrentCamId] = useState(0);
    const [ready, setReady] = useState(false);
    const [currentPlaylist, setCurrentPlaylist] = useState(convertCameraToPlaylist(cameras[0]));
    const lastPositionRef = useRef(0);
    registerHandler({
        id: 'openFeedbackPopup',
        handler: () => {
            if ($watchedSecondsAtom.get() >= ENV.FEEDBACK_FORM_DISPLAY_THRESHHOLD) {
                jwplayer.pause();
                const duration = getPlayerDuration();
                const position = getPlayerPosition();
                triggerFeedbackPopup(duration, position);
            }
        },
        context: ['onDesktop', 'onTrigger'],
    });
    useEffect(() => {
        return unsubscribe;
    }, []);
    useEffect(() => {
        if (currentCamId === 0 && cameras.length > 0) {
            setCurrentCamId(cameras[0].cameraId);
            fullscreenCurrentCamId = cameras[0].cameraId;
        }
    }, [cameras.length]);
    useEffect(() => {
        if (eventStatus === Status.streaming && jwplayerError) {
            setJwplayerError('');
        }
        // @ts-ignore
        Api.Viewer._requestCamList(eventId, (responseJSON) => {
            const result = responseJSON?.result?.streamURLs ?? responseJSON?.streamURLs;
            setCamList(result);
            setCurrentPlaylist(convertCameraToPlaylist(result[0]));
            setReady(true);
        });
    }, [eventStatus]);
    useEffect(() => {
        if (cameras.length > 0 && currentCamId !== 0) {
            const currentCamera = cameras.filter((camera) => currentCamId === camera.cameraId)[0];
            if (currentCamera.status !== currentPlaylist.status) {
                setCurrentPlaylist(convertCameraToPlaylist(currentCamera));
            }
        }
    }, [cameras]);
    const handleCameraSwitch = (data) => {
        try {
            // check if the source is current one
            if (fullscreenCurrentCamId === data.cameraId)
                return null;
            const toCamId = data.cameraId;
            Api.Analytics.camera(currentCamId, toCamId);
            setCurrentCamId(data.cameraId);
            fullscreenCurrentCamId = data.cameraId;
            if (!jwplayerError &&
                currentPlaylist.status !== Status.streamingFailed &&
                currentPlaylist.file &&
                jwplayer) {
                // update the playlist store and get new seekpoint
                helloSeekPoint = jwplayer?.getPosition();
                // reload the player
                jwplayer?.load(convertCameraToPlaylist(data));
            }
            // update current playlist
            setCurrentPlaylist(convertCameraToPlaylist(data));
            setJwplayerError('');
            cameras.forEach((camera) => {
                const cameraButton = document.querySelector(`#mobile-camera-button-${camera.cameraId}`);
                if (cameraButton) {
                    if (camera.cameraId === fullscreenCurrentCamId) {
                        cameraButton.classList.remove('not-selected');
                        cameraButton.innerHTML = `
            <span class="icon left">
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="fas"
                data-icon="check"
                class="svg-inline--fa fa-check "
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512">
                <path
                  fill="currentColor"
                  d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"></path>
              </svg>
            </span>
            ${camera.cameraTitle}
          `;
                    }
                    else if (!cameraButton.classList.contains('not-selected')) {
                        cameraButton.classList.add('not-selected');
                        cameraButton.innerHTML = `
            <span class="icon left">
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="fas"
                data-icon="video"
                class="svg-inline--fa fa-video "
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 576 512">
                <path
                  fill="currentColor"
                  d="M0 128C0 92.7 28.7 64 64 64H320c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2V384c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1V320 192 174.9l14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z"></path>
              </svg>
            </span>
            ${camera.cameraTitle}
          `;
                    }
                }
            });
        }
        catch (error) {
            console.error('>>>>>> handleCameraSwitch <<<<<<', error);
        }
    };
    const onTime = (event) => {
        const { position } = event;
        // Update watched seconds
        if (position > lastPositionRef.current) {
            const increment = position - lastPositionRef.current;
            const currentWatchedSeconds = $watchedSecondsAtom.get();
            $watchedSecondsAtom.set(currentWatchedSeconds + increment);
        }
        lastPositionRef.current = position;
    };
    const onReady = ({ player, id }) => {
        jwplayer = player;
        console.log('>>> on ready <<<', jwplayer);
        setPlayerId(id);
        setTimeToWatchCamera(new Date().getTime());
        jwplayer.on('time', onTime);
    };
    jwplayer?.on('playlist', () => {
        const state = jwplayer.getState();
        if (state === undefined || state === 'paused' || state === 'idle') {
            jwplayer.play();
        }
    });
    jwplayer?.on('idle', (event) => {
        if (event.oldstate === 'playing' && event.newstate === 'idle') {
            jwplayer.play();
        }
    });
    const onPlay = () => {
        Api.Analytics.streaming(currentCamId, currentCamId);
        Api.Viewer.requestAbort();
        setJwplayerError('');
        hideShowElementsOnPlayOrPause(true);
        $isPausedAtom.set(false);
        $shouldDisplayFeedbackAtom.set(false);
        clearInterval(intervalStreamingId);
        if (pauseTimeoutId) {
            clearInterval(pauseTimeoutId);
        }
    };
    const getPlayerPosition = useCallback(() => {
        return eventStatus === (Status.streaming || Status.streamingFailed)
            ? getTimeElapsed(startTime)
            : jwplayer?.getPosition() || 0;
    }, [eventStatus]);
    const getPlayerDuration = useCallback(() => {
        return eventStatus === (Status.streaming || Status.streamingFailed)
            ? eventDuration
            : jwplayer?.getDuration() || 0;
    }, []);
    const triggerFeedbackPopup = (duration, position) => {
        document.addEventListener('keydown', stopJWPlayerKeyboardEvents, true);
        $shouldDisplayFeedbackAtom.set(true);
        Api.Analytics.setPostData({
            eventStatus,
            position: duration > 0 ? ((position / duration) * 100).toFixed(2) : 0,
        });
    };
    const onPause = () => {
        Api.Analytics.pause(currentCamId, currentCamId);
        const timeoutId = window.setTimeout(() => {
            const position = getPlayerPosition();
            const duration = getPlayerDuration();
            if (!registrationRequired &&
                (eventStatus === Status.archived || eventStatus === Status.archivedS3) &&
                $watchedSecondsAtom.get() >= ENV.FEEDBACK_FORM_DISPLAY_THRESHHOLD) {
                triggerFeedbackPopup(duration, position);
            }
        }, 5000);
        setPauseTimeoutId(timeoutId);
        $isPausedAtom.set(true);
    };
    const onStop = () => {
        console.log('JWplayer API: onStop');
        Api.Analytics.stop(currentCamId, currentCamId);
        clearInterval(intervalStreamingId);
    };
    const onError = (event) => {
        console.error('>>>>>>>>>> REACTJS: error >>>>>>>>>>>');
        Api.Analytics.error(currentCamId, currentCamId, event.message);
        const workingCams = getValidCameras(cameras);
        if (workingCams.length === 0 || cameras.length === 1) {
            setJwplayerError(ERROR_MESSAGES.NO_CONNECTION);
        }
        else if (currentCamId === cameras[0].cameraId) {
            setJwplayerError(ERROR_MESSAGES.MAIN_CAMERA_UNAVAILABLE);
        }
        else {
            setJwplayerError(ERROR_MESSAGES.CAMERA_UNAVAILABLE);
        }
    };
    const onSetupError = (event) => {
        console.error('>>>>>>>>>> REACTJS: this is setup error >>>>>>>>>>>', event.message);
        Api.Analytics.error(currentCamId, currentCamId, event.message);
        const workingCams = getValidCameras(cameras);
        if (workingCams.length === 0 || cameras.length === 1) {
            setJwplayerError(ERROR_MESSAGES.NO_CONNECTION);
        }
        else if (currentCamId === cameras[0].cameraId) {
            setJwplayerError(ERROR_MESSAGES.MAIN_CAMERA_UNAVAILABLE);
        }
        else {
            setJwplayerError(ERROR_MESSAGES.CAMERA_UNAVAILABLE);
        }
    };
    const onBuffer = () => {
        console.log('JW player onBuffer');
        Api.Analytics.buffer(currentCamId, currentCamId);
        hideShowElementsOnPlayOrPause(true);
    };
    const onSeek = (event) => {
        console.log('JW player onSeek:' + event.position + '=' + event.offset);
        Api.Analytics.seek(currentCamId, currentCamId, event.position, event.offset);
    };
    const onMute = (event) => {
        console.log('onMute', event.mute);
        Api.Analytics.mute(event.mute, currentCamId, currentCamId);
    };
    const onFullscreen = (event) => {
        console.log('onFullscreen', event.fullscreen);
        Api.Analytics.fullscreen(event.fullscreen, currentCamId, currentCamId);
        const adsContainer = document.querySelector('.afs_ads.ad-placement');
        const mobileHeader = document.querySelector('#fullscreen-mobile-header');
        if (adsContainer && !mobileHeader) {
            const mobileHeader = generateMobileHeader(eventImage, eventTitle, cameras, currentCamId, handleCameraSwitch, showMobileTechSupport, eventSubHeading);
            adsContainer.appendChild(mobileHeader);
        }
        $isFullscreenAtom.set(event.fullscreen);
    };
    const showMobileTechSupport = () => {
        const techSupportContent = document.querySelector('#tech-support-container');
        if (techSupportContent?.classList.contains('tech-support-visible')) {
            techSupportContent.classList.remove('tech-support-visible');
        }
        else {
            techSupportContent?.classList.add('tech-support-visible');
        }
    };
    const onFirstFrame = () => {
        console.log('JW player onFirstFrame', helloSeekPoint);
        jwplayer?.seek(helloSeekPoint);
        if (isMobile) {
            unmute();
        }
    };
    const onComplete = () => {
        console.log('JW player onComplate');
        Api.Analytics.stop(currentCamId, currentCamId);
        helloSeekPoint = 0; // reset the seekpoint
        onStop();
        if (onDemand && rewind) {
            rewind();
        }
    };
    const unmute = () => {
        console.log('JW player unmute');
        if (jwplayer.getMute()) {
            jwplayer.setMute(false);
        }
    };
    // JW Player has its own keyboard listeners.
    // e.g. "f" = toggle full screen, "space" = play/pause
    // This interfers with feedback form keyboard typing thus need to be disabled when feedback form is visible
    const stopJWPlayerKeyboardEvents = (event) => {
        const shouldDisplay = $shouldDisplayFeedbackAtom.get();
        if (shouldDisplay) {
            event.stopPropagation();
        }
    };
    const enableJWPlayerKeyboardEvents = () => {
        document.removeEventListener('keydown', stopJWPlayerKeyboardEvents, true);
    };
    return {
        currentCamId,
        currentPlaylist,
        jwplayerError,
        ready,
        playerId,
        enableJWPlayerKeyboardEvents,
        handleCameraSwitch,
        onBuffer,
        onComplete,
        onError,
        onFirstFrame,
        onFullscreen,
        onMute,
        onPause,
        onPlay,
        onReady,
        onSeek,
        onSetupError,
        onStop,
    };
}
